/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { createUserDateTimeFormatter } from '../utils/date-time.utils';

const DATE_FORMAT_KEY = 'qubepro_userSetting_dateFormat';
const TIME_FORMAT_KEY = 'qubepro_userSetting_timeFormat';

function readCachedDateFormat(): string | null {
  try {
    const v = (localStorage.getItem(DATE_FORMAT_KEY) || '').trim();
    return v || null;
  } catch {
    return null;
  }
}

function readCachedTimeFormat(): '12h' | '24h' | null {
  try {
    const v = (localStorage.getItem(TIME_FORMAT_KEY) || '').trim().toLowerCase();
    return v === '12h' || v === '24h' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Proje genelinde merkezi tarih/saat formatı.
 *
 * Veri kaynağı önceliği:
 *  1. localStorage (UiPreferencesGate bootstrap'te ve Settings kaydında yazılır)
 *  2. user.dateFormat/timeFormat (AuthContext /auth/me'den gelen)
 *
 * Bu hook API çağrısı YAPMAZ. Güncel değer zaten:
 *  - Login sonrası UiPreferencesGate tarafından tek seferde çekilip localStorage'a yazılır
 *  - Settings sayfasında ayar kaydedilince localStorage'a yazılır + custom event yayınlanır
 *  - Başka tab'da localStorage değişirse storage event ile güncellenir
 */
export function useDateTime() {
  const { user, language } = useAuth();

  const userDateFormat =
    (user as any)?.dateFormat ??
    (user as any)?.date_format ??
    (user as any)?.settings?.dateFormat ??
    null;
  const userTimeFormat =
    (user as any)?.timeFormat ??
    (user as any)?.time_format ??
    (user as any)?.settings?.timeFormat ??
    (user as any)?.settings?.time_format ??
    null;

  const [dateFormat, setDateFormat] = useState<string | null>(() => {
    return readCachedDateFormat() ?? (typeof userDateFormat === 'string' ? userDateFormat.trim() || null : null);
  });
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h' | null>(() => {
    const userValue = typeof userTimeFormat === 'string' ? userTimeFormat.trim().toLowerCase() : '';
    return readCachedTimeFormat() ?? (userValue === '12h' || userValue === '24h' ? userValue : null);
  });

  // user profile güncellenince (refreshUser sonrası) state'i güncelle
  useEffect(() => {
    if (typeof userDateFormat === 'string' && userDateFormat.trim().length > 0) {
      setDateFormat(prev => (prev === userDateFormat ? prev : userDateFormat));
      try { localStorage.setItem(DATE_FORMAT_KEY, userDateFormat); } catch { /* yoksay */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDateFormat]);

  useEffect(() => {
    if (typeof userTimeFormat !== 'string') return;
    const next = userTimeFormat.trim().toLowerCase();
    if (next !== '12h' && next !== '24h') return;
    setTimeFormat(prev => (prev === next ? prev : next));
    try { localStorage.setItem(TIME_FORMAT_KEY, next); } catch { /* yoksay */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTimeFormat]);

  // Başka tab'da localStorage güncellenirse bu tab da güncellesin
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== DATE_FORMAT_KEY && e.key !== TIME_FORMAT_KEY) return;
      const v = typeof e.newValue === 'string' ? e.newValue.trim() : '';
      if (e.key === DATE_FORMAT_KEY) {
        setDateFormat(v || null);
      } else {
        const next = v.toLowerCase();
        setTimeFormat(next === '12h' || next === '24h' ? next : null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Aynı tab'da ayar ekranı custom event yayınlarsa anında uygula
  useEffect(() => {
    const onSettingChanged = (event: Event) => {
      const e = event as CustomEvent<{ key?: string; value?: unknown }>;
      const key = (e.detail?.key || '').trim();
      if (key !== 'dateFormat' && key !== 'date_format' && key !== 'timeFormat' && key !== 'time_format') return;

      const value = typeof e.detail?.value === 'string' ? e.detail.value.trim() : '';
      if ((key === 'dateFormat' || key === 'date_format') && value) {
        setDateFormat(prev => (prev === value ? prev : value));
        try { localStorage.setItem(DATE_FORMAT_KEY, value); } catch { /* yoksay */ }
      } else if (key === 'timeFormat' || key === 'time_format') {
        const next = value.toLowerCase();
        if (next === '12h' || next === '24h') {
          setTimeFormat(prev => (prev === next ? prev : next));
          try { localStorage.setItem(TIME_FORMAT_KEY, next); } catch { /* yoksay */ }
        }
      }
    };

    window.addEventListener('qubepro-user-setting-changed', onSettingChanged as EventListener);
    return () => window.removeEventListener('qubepro-user-setting-changed', onSettingChanged as EventListener);
  }, []);

  const userLanguage = (user as any)?.language ?? language;
  const userTimeZone = (user as any)?.timezone ?? (user as any)?.timeZone ?? null;
  const effectiveDateFormat = readCachedDateFormat() ?? dateFormat ?? null;
  const effectiveTimeFormat = readCachedTimeFormat() ?? timeFormat ?? '24h';

  return useMemo(() => {
    return createUserDateTimeFormatter({
      language: userLanguage,
      timeZone: userTimeZone,
      dateFormat: effectiveDateFormat,
      timeFormat: effectiveTimeFormat,
    });
  }, [userLanguage, userTimeZone, effectiveDateFormat, effectiveTimeFormat]);
}
